import * as React from 'react';
import { Article, ArticleChangeImportance, ArticleContact, defaultArticlePublishedEmailTemplate, fieldValidation, ReleaseRecord } from '../../../../../amorg-common';
import { Button } from '../../../../../designSystem/button/Button';
import { AnimatedCollapsibleContent } from '../../../../../designSystem/collapsible/AnimatedCollapsibleContent';
import { colors } from '../../../../../designSystem/colors/color';
import { Flex } from '../../../../../designSystem/compositeComponents/FlexHelpers';
import { ImportanceAvatar } from '../../../../../designSystem/avatar/ImportanceAvatar';
import { ListRowWithChildren, ListRow } from '../../../../../designSystem/compositeComponents/ListRow';
import { Divider } from '../../../../../designSystem/Divider';
import { FormField } from '../../../../../designSystem/formField/FormField';
import { P } from '../../../../../designSystem/typography/Body';
import { H3, H4 } from '../../../../../designSystem/typography/Heading';
import { ArticleViewContactsPanel } from './ArticleViewContactsPanel';
import { ArticleViewReleaseEmails } from './ArticleViewReleaseEmails';
import { articlesClientApi } from '../../../../../api/articlesClientApi';
import { ButtonRow } from '../../../../../designSystem/button/ButtonRow';
import { TextAreaSaveField } from '../../../../../designSystem/formField/textArea/TextAreaSaveField';
import { Checkbox } from '../../../../../designSystem/inputs/Checkbox';
import { HtmlEditorWithSave } from '../../../../../designSystem/inputs/HtmlEditorWithSave';
import { LoadingPageOverlay } from '../../../../../designSystem/LoadingPageOverlay';
import { MessageCard } from '../../../../../designSystem/Message';
import { Modal } from '../../../../../designSystem/Modal';
import { ArticleViewImportanceSelector } from './ArticleViewImportanceSelector';

type ArticleViewReleasePanelProps = {
    article: Article;
    releaseRecords: ReleaseRecord[];
    contacts: ArticleContact[];
    onChanged: () => void;
};

export function ArticleViewReleasePanel (props: ArticleViewReleasePanelProps): JSX.Element
{
    const [expanded, setExpanded] = React.useState<boolean>(false);

    React.useEffect(() =>
    {
        if (props.releaseRecords.length > 0)
            setExpanded(true);
    }, [props.releaseRecords]);

    function isCustomized (record: ReleaseRecord): boolean
    {
        return record.customImportance !== props.article.importance
            || record.customChangeDescription !== ''
            || record.customAssessment !== ''
            || record.customEmailTemplate !== '';
    }

    return (
        <Flex flexDirection="column">
            <Flex>
                <Flex gap={8} style={{ flexGrow: 1 }}>
                    <H3>Udgivelser</H3>
                    <ImportanceAvatar text={props.releaseRecords.length.toString()} fontSize='12px' size='25px' importance={props.article.importance} />
                </Flex>
                <Button variant='text' icon={expanded ? 'caret_caretUp' : 'caret_caretDown'} onClick={() => setExpanded(!expanded)} />
            </Flex>
            <AnimatedCollapsibleContent open={expanded}>

                <Flex flexDirection='column' gap={32}>
                    <Divider style={{ marginTop: '10px' }} />
                    <Flex flexDirection="column" gap={2}>
                        {props.releaseRecords.length === 0 &&
                            <div>Der er ingen udgivelser</div>}
                        {props.releaseRecords.map((record) =>
                        {
                            const customized = isCustomized(record);

                            const subscriberContacts = props.contacts.filter(c => c.subscriberId === record.subscriber.id);

                            return (
                                <div key={record.id}>
                                    <ListRowWithChildren styleVariant='neutral' stripColor={customized ? colors.icon.active : colors.icon.passive} expanded={customized} onCollapse={() => { return; }}
                                        content={<Flex alignItems='center'>
                                            <Flex style={{ flexGrow: 1 }} gap={8} alignItems='center'>
                                                <ImportanceAvatar importance={record.customImportance} />
                                                <H4>
                                                    {record.subscriber.siteId}
                                                </H4>
                                                {subscriberContacts.length === 0 &&
                                                    <span>(ingen kontaktpersoner tilknyttet!)</span>
                                                }
                                                {customized && <span>(tilpasset)</span>}
                                            </Flex>
                                            {props.article.release === null &&
                                                <EditReleaseModal
                                                    siteId={record.subscriber.siteId}
                                                    release={record}
                                                    onChanged={props.onChanged} />}
                                        </Flex>}
                                        childRows={<>
                                            {props.article.release
                                                ? <ArticleViewReleaseEmails emails={record.emails} />
                                                : <ArticleViewContactsPanel contacts={subscriberContacts} />}

                                            {record.customEmailTemplate !== '' &&
                                                <ListRow level='level2' styleVariant="neutral" stripColor={colors.icon.active}>
                                                    <FormField
                                                        label="Tilpasset e-mail"
                                                        inputComponent={<P dangerouslySetInnerHTML={{ __html: record.customEmailTemplate }} />} />
                                                </ListRow>}

                                            {record.customChangeDescription !== '' &&
                                                <ListRow level='level2' styleVariant="neutral" stripColor={colors.icon.active}>
                                                    <FormField
                                                        label="Tilpasset ændringsbeskrivelse"
                                                        inputComponent={<P dangerouslySetInnerHTML={{ __html: record.customChangeDescription }} />} />
                                                </ListRow>}

                                            {record.customAssessment !== '' &&
                                                <ListRow level='level2' styleVariant="neutral" stripColor={colors.icon.active}>
                                                    <FormField
                                                        label="Tilpasset vurdering"
                                                        inputComponent={<P dangerouslySetInnerHTML={{ __html: record.customAssessment }} />} />
                                                </ListRow>}
                                        </>} />
                                </div>
                            );
                        }
                        )}
                    </Flex>

                </Flex>
            </AnimatedCollapsibleContent>
        </Flex>
    );
}


type EditReleaseModalButtonProps = {
    siteId: string,
    release: ReleaseRecord,
    onChanged: () => void;
};
export function EditReleaseModal (props: EditReleaseModalButtonProps): JSX.Element
{
    const [show, setShow] = React.useState<boolean>(false);
    const [loading, setLoading] = React.useState(false);

    async function handleChangeImportance (importance: ArticleChangeImportance)
    {
        setLoading(true);
        await articlesClientApi.updateReleaseRecord(
            props.release.id,
            props.release.customChangeDescription,
            props.release.customAssessment,
            importance,
            props.release.customEmailTemplate
        );
        props.onChanged();
        setLoading(false);
    }

    async function handleUpdate (customChangeDescription: string, customAssessment: string, isValid: boolean)
    {
        if (!isValid)
            return;

        setLoading(true);
        await articlesClientApi.updateReleaseRecord(
            props.release.id,
            customChangeDescription,
            customAssessment,
            props.release.customImportance,
            props.release.customEmailTemplate
        );
        props.onChanged();
        setLoading(false);
    }

    return (
        <>
            <LoadingPageOverlay loading={loading} />
            <Button variant='text' icon='interface_actions_edit' onClick={() => setShow(true)} />
            <Modal showModal={show} title={`Rediger udgivelse for ${props.siteId}`} onCancel={() => setShow(false)}
                content={
                    <>
                        <Flex flexDirection='column' gap={16}>
                            <Flex gap={16}>
                                <div style={{ flexGrow: 1 }}>
                                    <ArticleViewImportanceSelector groupName='customImportance' importance={props.release.customImportance} onImportanceChange={handleChangeImportance} />
                                </div>
                            </Flex>

                            <EmailTemplateField record={props.release} onChanged={props.onChanged} />

                            <TextAreaSaveField
                                label='Tilpasset ændringsbeskrivelse'
                                minRows={3}
                                value={props.release.customChangeDescription}
                                validator={(value) =>
                                    fieldValidation.articleReleaseRecord.customChangeDescription(value)
                                        ? { valid: true }
                                        : {
                                            valid: false, errorMessage: `Maks. 5000 tegn (${value.length})`
                                        }}
                                onSave={(value, isValid) => handleUpdate(value, props.release.customAssessment, isValid)} />

                            <TextAreaSaveField
                                label='Tilpasset vurdering'
                                minRows={3}
                                value={props.release.customAssessment}
                                validator={(value) =>
                                    fieldValidation.articleReleaseRecord.customAssessment(value)
                                        ? { valid: true }
                                        : {
                                            valid: false, errorMessage: `Maks. 5000 tegn (${value.length})`
                                        }}
                                onSave={(value, isValid) => handleUpdate(props.release.customChangeDescription, value, isValid)} />

                            <ButtonRow>
                                <Button variant='outlined' text='Luk' onClick={() => setShow(false)} />
                            </ButtonRow>
                        </Flex>
                    </>
                } />
        </>
    );
}

type EmailTemplateFieldProps = {
    record: ReleaseRecord,
    onChanged: () => void;
};
function EmailTemplateField (props: EmailTemplateFieldProps): JSX.Element
{
    const [customized, setCustomized] = React.useState<boolean>(false);
    const [error, setError] = React.useState<boolean>(false);

    React.useEffect(() =>
    {
        setCustomized(props.record.customEmailTemplate !== '');
    }, [props.record.customEmailTemplate]);



    async function handleCustomizedCheckedChange (value: string, checked: boolean)
    {
        if (checked)
        {
            await onSaveEmail(defaultArticlePublishedEmailTemplate);
        }
        else 
        {
            await onSaveEmail('');
        }
    }

    async function onSaveEmail (content: string): Promise<boolean>
    {
        const result = await articlesClientApi.updateReleaseRecord(
            props.record.id,
            props.record.customChangeDescription,
            props.record.customAssessment,
            props.record.customImportance,
            content
        );
        if (result.status === 'success')
        {
            props.onChanged();
            setError(false);
            return true;
        }
        else
        {
            setError(true);
            return false;
        }
    }

    const tagReplacementToggleTip =
        <span>
            <p>Der kan benyttes følgende erstatningstekst i feltet:</p>

            <p>
                <b>{'{company.name}'}</b><br />
                Indsætter organisationens fulde navn hvis defineret. Ellers organisationens id.<br />
                <b>{'{contactperson.firstname}'}</b><br />
                Indsætter fornavn på modtageren.<br />
                <b>{'{contactperson.fullname}'}</b><br />
                Indsætter fulde navn på modtageren.<br />
                <b>{'{article.title}'}</b><br />
                Indsætter lovkravets titel.<br />
                <b>{'{article.listname}'}</b><br />
                Indsætter navnet på den liste lovkravet er tilknyttet.<br />
                <b>{'{article.url}'}</b><br />
                Indsætter URL til lovkravet.<br />
            </p>
        </span>;

    return (
        <div>
            {error &&
                <MessageCard variant='error'>
                    Kunne ikke opdateret e-mail teksten. Kontrollér at teksten ikke overstiger 5000 tegn, og at alle erstatningsfelter er korrekt udformet.
                </MessageCard>
            }
            <FormField
                label="Tilpasset e-mail skabelon"
                toolTip={tagReplacementToggleTip}
                inputComponent={
                    <HtmlEditorWithSave
                        id={props.record.id + '_email_template_editor'}
                        value={props.record.customEmailTemplate}
                        onSave={onSaveEmail}
                        onCancel={() => setError(false)}
                        readonly={!customized} />
                }
            />

            <Checkbox text='Tilpasset email skabelon' value="customized"
                checked={customized}
                onChange={handleCustomizedCheckedChange} />
        </div>
    );
}